import React from "react";
import { ExternalLink, Sparkles } from "lucide-react";

import type { SectorNewsItem, SectorPanelData } from "./HotTopicsSection.types";
import { formatPublishTime, formatTickers } from "./HotTopicsSection.utils";

export function SectorNewsPanel({
  panel,
  loading,
  theme = "dark",
  hideSentiment = false,
  onAnalyzeNews,
}: {
  panel?: SectorPanelData;
  loading?: boolean;
  theme?: "light" | "dark";
  hideSentiment?: boolean;
  onAnalyzeNews?: (news: SectorNewsItem, panel: SectorPanelData) => void;
}) {
  const isDark = theme === "dark";
  const hasSentiment =
    !hideSentiment &&
    ((panel?.bullish?.length ?? 0) > 0 || (panel?.bearish?.length ?? 0) > 0);

  if (loading && !panel) {
    return (
      <div
        className={`h-[360px] rounded-2xl border p-4 ${
          isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
        }`}
      >
        <div
          className={`h-4 w-40 rounded ${
            isDark ? "bg-white/10" : "bg-slate-100"
          }`}
        />
        <div
          className={`mt-3 h-3 w-56 rounded ${
            isDark ? "bg-white/10" : "bg-slate-100"
          }`}
        />
        <div className="mt-5 space-y-3">
          <div
            className={`h-12 rounded-xl ${
              isDark ? "bg-white/5" : "bg-slate-50"
            }`}
          />
          <div
            className={`h-12 rounded-xl ${
              isDark ? "bg-white/5" : "bg-slate-50"
            }`}
          />
          <div
            className={`h-12 rounded-xl ${
              isDark ? "bg-white/5" : "bg-slate-50"
            }`}
          />
        </div>
      </div>
    );
  }

  const safe = panel;
  if (!safe) return null;

  return (
    <div
      className={`rounded-2xl border p-4 ${
        isDark
          ? "border-white/10 bg-white/5 shadow-[0_18px_60px_-44px_rgba(0,0,0,0.9)]"
          : "border-slate-200 bg-white shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className={`truncate text-sm font-semibold ${
              isDark ? "" : "text-slate-900"
            }`}
          >
            {safe.label}
          </div>
          <div
            className={`mt-1 text-[11px] ${
              isDark ? "text-white/55" : "text-slate-500"
            }`}
          >
            Top {safe.count} news
          </div>
        </div>
      </div>

      {hasSentiment && (
        <div className="mt-4 space-y-2">
          <div
            className={`rounded-lg border p-3 ${
              isDark
                ? "border-rose-400/15 bg-rose-400/10"
                : "border-rose-200 bg-rose-50"
            }`}
          >
            <div
              className={`text-[11px] font-semibold ${
                isDark ? "text-rose-200" : "text-rose-700"
              }`}
            >
              Bearish:
            </div>
            <div
              className={`mt-1 text-[11px] leading-relaxed ${
                isDark ? "text-white/60" : "text-rose-900/80"
              }`}
            >
              {formatTickers(safe.bearish)}
            </div>
          </div>

          <div
            className={`rounded-lg border p-3 ${
              isDark
                ? "border-emerald-400/15 bg-emerald-400/10"
                : "border-emerald-200 bg-emerald-50"
            }`}
          >
            <div
              className={`text-[11px] font-semibold ${
                isDark ? "text-emerald-200" : "text-emerald-700"
              }`}
            >
              Bullish:
            </div>
            <div
              className={`mt-1 text-[11px] leading-relaxed ${
                isDark ? "text-white/60" : "text-emerald-900/80"
              }`}
            >
              {formatTickers(safe.bullish)}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {safe.news.length === 0 ? (
          <div
            className={`rounded-xl border p-3 text-xs ${
              isDark
                ? "border-white/10 bg-white/5 text-white/60"
                : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
          >
            No news available.
          </div>
        ) : (
          safe.news.map((n, idx) => (
            <a
              key={`${safe.sectorId}-${idx}-${n.url}`}
              href={n.url}
              target="_blank"
              rel="noreferrer noopener"
              className={`group relative block rounded-xl border p-3 transition ${
                isDark
                  ? "border-white/10 bg-white/5 hover:bg-white/8"
                  : "border-slate-200 bg-slate-50 hover:border-blue-300 hover:shadow-md hover:bg-white"
              }`}
              aria-label={n.title}
            >
              {onAnalyzeNews && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAnalyzeNews(n, safe);
                  }}
                  className={`absolute top-2 right-2 flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition-all z-10 ${
                    isDark
                      ? "border-white/20 bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                      : "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:border-blue-300"
                  }`}
                  title="AI Analysis"
                >
                  <Sparkles className="h-3 w-3" />
                  AI
                </button>
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className={`text-[11px] ${
                      isDark
                        ? "text-white/55"
                        : "font-medium text-slate-500 uppercase tracking-wider"
                    }`}
                  >
                    {formatPublishTime(n.publish_time)}
                  </div>
                  <div
                    className={`mt-1 line-clamp-2 text-sm font-medium ${
                      isDark ? "text-white/85" : "text-slate-800 group-hover:text-blue-700"
                    }`}
                  >
                    {n.title}
                  </div>
                </div>
                <ExternalLink
                  className={`mt-1 h-4 w-4 shrink-0 transition ${
                    isDark
                      ? "text-white/45 group-hover:text-white/70"
                      : "text-slate-400 group-hover:text-blue-500"
                  }`}
                />
              </div>

              {n.summary ? (
                <div
                  className={`mt-2 line-clamp-3 text-xs ${
                    isDark ? "text-white/65" : "text-slate-500"
                  }`}
                >
                  {n.summary}
                </div>
              ) : null}
            </a>
          ))
        )}
      </div>
    </div>
  );
}
